import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Autocomplete,
  Alert,
  Divider,
  Card,
  CardContent,
} from "@mui/material";
import { Search, Download, Refresh } from "@mui/icons-material";
import axios from "axios";
import { format } from "date-fns";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import jsPDF from "jspdf";
import "jspdf-autotable";

const SubaccountLedger = () => {
  const { userInfo } = useContext(AutoTaskAuthContext) || {};
  const store_id = userInfo?.store_id;

  // ─── State ────────────────────────────────────────────────────────────────
  const [costCenters, setCostCenters] = useState([]);
  const [selectedCostCenter, setSelectedCostCenter] = useState(null);
  const [loadingCostCenters, setLoadingCostCenters] = useState(false);

  const [fromDate, setFromDate] = useState(
    format(
      new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      "yyyy-MM-dd",
    ),
  );
  const [toDate, setToDate] = useState(format(new Date(), "yyyy-MM-dd"));

  const [loadingReport, setLoadingReport] = useState(false);
  const [ledgerData, setLedgerData] = useState(null);
  const [error, setError] = useState("");

  // ─── Fetch Cost Centers (deduplicated by name) ────────────────────────────
  const fetchCostCenters = async () => {
    if (store_id === null || store_id === undefined) return;
    try {
      setLoadingCostCenters(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/accounts/cost-centers/${store_id}`,
      );
      if (response.data.success) {
        const seenNames = new Set();
        const unique = (response.data.data || []).filter((cc) => {
          if (seenNames.has(cc.name)) return false;
          seenNames.add(cc.name);
          return true;
        });
        setCostCenters(unique);
      }
    } catch (err) {
      console.error("Error fetching cost centers:", err);
      setError("Error loading cost centers");
    } finally {
      setLoadingCostCenters(false);
    }
  };

  useEffect(() => {
    if (store_id) fetchCostCenters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store_id]);

  // ─── Generate Report ──────────────────────────────────────────────────────
  const handleGenerateReport = async () => {
    setError("");

    if (!selectedCostCenter) {
      setError("Please select a cost center");
      return;
    }
    if (!fromDate || !toDate) {
      setError("Please select a date range");
      return;
    }

    try {
      setLoadingReport(true);

      // ✅ FIX: send costcenter_id as the numeric ID, not the name
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/accounts/sub-account-ledger`,
        {
          store_id: parseInt(store_id),
          costcenter_id: selectedCostCenter.id, // <--- ID instead of name
          from_date: fromDate,
          to_date: toDate,
        },
      );

      if (response.data.success) {
        setLedgerData(response.data.data);
      } else {
        setError(response.data.message || "Failed to generate report");
      }
    } catch (err) {
      console.error("Error generating report:", err);
      setError(err.response?.data?.message || "Error generating ledger report");
    } finally {
      setLoadingReport(false);
    }
  };

  // ─── Reset ────────────────────────────────────────────────────────────────
  const handleReset = () => {
    setSelectedCostCenter(null);
    setLedgerData(null);
    setError("");
  };

  // ─── Helpers ──────────────────────────────────────────────────────────────
  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount);

  const formatAmount = (amount) =>
    new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);

  const isPartyGroup = (group) => group.account_name?.toLowerCase() === "party";

  // Reusable DEBIT / CREDIT badge — always primary color (blue)
  const BalanceBadge = ({ type }) => (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        ml: 0.5,
        px: 0.5,
        py: 0.5,
        borderRadius: 20,
        fontSize: "0.6rem",
        fontWeight: 600,
        lineHeight: 1,
        color: "white",
        backgroundColor: "primary.main",
      }}
    >
      {type}
    </Box>
  );

  // ─── Grand totals from API response ──────────────────────────────────────
  const grandTotals = ledgerData
    ? {
        totalDebit: ledgerData.grand_total_debit,
        totalCredit: ledgerData.grand_total_credit,
        transactionCount: ledgerData.total_transaction_count,
      }
    : null;

  // ─── PDF Export ───────────────────────────────────────────────────────────
  const generatePDF = () => {
    if (!ledgerData) return;

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 10;
    const storeName = userInfo?.store_name || "";
    const costCenterName =
      selectedCostCenter?.name || `Cost Center ${ledgerData.costcenter_id}`;

    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text(storeName, pageWidth / 2, 12, { align: "center" });

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(`Cost Center: ${costCenterName}`, pageWidth / 2, 18, {
      align: "center",
    });

    doc.setFontSize(8);
    doc.text(
      `Statement Of Account: ${format(
        new Date(fromDate),
        "dd-MM-yyyy",
      )} to ${format(new Date(toDate), "dd-MM-yyyy")}`,
      margin,
      24,
    );
    doc.text("Page No 1 of 1", pageWidth - margin - 20, 24);

    let startY = 28;

    ledgerData.groups.forEach((group, gIdx) => {
      const showPartyCode = isPartyGroup(group);
      const tableData = [];

      if (showPartyCode) {
        tableData.push([
          "",
          "",
          "Opening Balance",
          formatAmount(group.opening_balance),
          "",
          `${formatAmount(group.opening_balance)}${
            group.opening_balance_type === "DEBIT" ? "Db" : "Cr"
          }`,
        ]);
      } else {
        tableData.push([
          "",
          "Opening Balance",
          formatAmount(group.opening_balance),
          "",
          `${formatAmount(group.opening_balance)}${
            group.opening_balance_type === "DEBIT" ? "Db" : "Cr"
          }`,
        ]);
      }

      group.transactions.forEach((txn) => {
        if (showPartyCode) {
          tableData.push([
            format(new Date(txn.date), "dd-MM-yyyy"),
            txn.party_code || "-",
            txn.narration || "",
            txn.debit_amount > 0 ? formatAmount(txn.debit_amount) : "",
            txn.credit_amount > 0 ? formatAmount(txn.credit_amount) : "",
            `${formatAmount(txn.running_balance)}${
              txn.balance_type === "DEBIT" ? "Db" : "Cr"
            }`,
          ]);
        } else {
          tableData.push([
            format(new Date(txn.date), "dd-MM-yyyy"),
            txn.narration || "",
            txn.debit_amount > 0 ? formatAmount(txn.debit_amount) : "",
            txn.credit_amount > 0 ? formatAmount(txn.credit_amount) : "",
            `${formatAmount(txn.running_balance)}${
              txn.balance_type === "DEBIT" ? "Db" : "Cr"
            }`,
          ]);
        }
      });

      if (showPartyCode) {
        tableData.push([
          "",
          "",
          "Closing Totals",
          formatAmount(group.total_debit),
          formatAmount(group.total_credit),
          `${formatAmount(group.closing_balance)}${
            group.closing_balance_type === "DEBIT" ? "Db" : "Cr"
          }`,
        ]);
      } else {
        tableData.push([
          "",
          "Closing Totals",
          formatAmount(group.total_debit),
          formatAmount(group.total_credit),
          `${formatAmount(group.closing_balance)}${
            group.closing_balance_type === "DEBIT" ? "Db" : "Cr"
          }`,
        ]);
      }

      const colCount = showPartyCode ? 6 : 5;
      const debitColIndex = showPartyCode ? 3 : 2;

      const columnHeaders = showPartyCode
        ? [
            { content: "Date", styles: { fontStyle: "bold" } },
            {
              content: "Party Code",
              styles: { fontStyle: "bold", whiteSpace: "nowrap" },
            },
            { content: "Narration", styles: { fontStyle: "bold" } },
            {
              content: "Debit",
              styles: { fontStyle: "bold", halign: "right" },
            },
            {
              content: "Credit",
              styles: { fontStyle: "bold", halign: "right" },
            },
            {
              content: "Balance",
              styles: { fontStyle: "bold", halign: "right" },
            },
          ]
        : [
            { content: "Date", styles: { fontStyle: "bold" } },
            { content: "Narration", styles: { fontStyle: "bold" } },
            {
              content: "Debit",
              styles: { fontStyle: "bold", halign: "right" },
            },
            {
              content: "Credit",
              styles: { fontStyle: "bold", halign: "right" },
            },
            {
              content: "Balance",
              styles: { fontStyle: "bold", halign: "right" },
            },
          ];

      const columnStyles = showPartyCode
        ? {
            0: { halign: "left", cellWidth: 22 },
            1: { halign: "left", cellWidth: 22 },
            2: { halign: "left", cellWidth: 61 },
            3: { halign: "right", cellWidth: 22 },
            4: { halign: "right", cellWidth: 22 },
            5: { halign: "right", cellWidth: 31 },
          }
        : {
            0: { halign: "left", cellWidth: 25 },
            1: { halign: "left", cellWidth: 83 },
            2: { halign: "right", cellWidth: 25 },
            3: { halign: "right", cellWidth: 25 },
            4: { halign: "right", cellWidth: 32 },
          };

      doc.autoTable({
        startY,
        head: [
          [
            {
              content: `${group.account_name}${
                group.account_category ? ` (${group.account_category})` : ""
              }`,
              colSpan: colCount,
              styles: {
                fontStyle: "bold",
                fillColor: [220, 220, 220],
                textColor: [0, 0, 0],
                fontSize: 9,
              },
            },
          ],
          columnHeaders,
        ],
        body: tableData,
        theme: "grid",
        styles: {
          fontSize: 8,
          cellPadding: 1.5,
          lineColor: [0, 0, 0],
          lineWidth: 0.1,
          textColor: [0, 0, 0],
          overflow: "linebreak",
        },
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          lineWidth: 0.3,
          fontSize: 8,
        },
        columnStyles,
        didParseCell: (data) => {
          if (data.section === "body") {
            if (data.row.index === 0) {
              data.cell.styles.fontStyle = "bold";
            }
            if (data.row.index === tableData.length - 1) {
              data.cell.styles.fontStyle = "bold";
              data.cell.styles.fillColor = [245, 245, 245];
            }
            if (
              data.column.index === debitColIndex &&
              data.cell.text[0] &&
              data.row.index !== 0 &&
              data.row.index !== tableData.length - 1
            ) {
              data.cell.styles.textColor = [220, 53, 69];
            }
          }
        },
        margin: { left: margin, right: margin },
      });

      startY =
        doc.lastAutoTable.finalY +
        (gIdx < ledgerData.groups.length - 1 ? 6 : 0);
    });

    const sanitized = costCenterName.replace(/[^a-zA-Z0-9]/g, "_");
    doc.save(
      `CostCenter_Ledger_${sanitized}_${format(
        new Date(fromDate),
        "dd-MM-yyyy",
      )}_to_${format(new Date(toDate), "dd-MM-yyyy")}.pdf`,
    );
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* ── Filters Panel ── */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          Report Filters
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Autocomplete
              options={costCenters}
              getOptionLabel={(option) =>
                option.name ? option.name : `Cost Center ${option.id}`
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={selectedCostCenter}
              onChange={(_, newValue) => {
                setSelectedCostCenter(newValue);
                setLedgerData(null);
                setError("");
              }}
              loading={loadingCostCenters}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Cost Center"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingCostCenters ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="date"
              label="From Date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="date"
              label="To Date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid
            item
            xs={12}
            md={2}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <Button
              variant="contained"
              size="large"
              fullWidth
              startIcon={
                loadingReport ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <Search />
                )
              }
              onClick={handleGenerateReport}
              disabled={loadingReport}
            >
              {loadingReport ? "Loading..." : "Generate"}
            </Button>
          </Grid>

          <Grid item xs={12}>
            <Button
              variant="outlined"
              size="large"
              startIcon={<Refresh />}
              onClick={handleReset}
            >
              Reset
            </Button>
          </Grid>
        </Grid>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>

      {/* ── Ledger Report ── */}
      {ledgerData && (
        <Paper elevation={2} sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              Cost Center Ledger Statement
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="body1">
                  <strong>Cost Center:</strong>{" "}
                  {selectedCostCenter?.name ||
                    `ID: ${ledgerData.costcenter_id}`}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body1">
                  <strong>Period:</strong>{" "}
                  {format(new Date(fromDate), "dd/MM/yyyy")} to{" "}
                  {format(new Date(toDate), "dd/MM/yyyy")}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Grand Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Grand Total Debit
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 600, color: "error.main" }}
                  >
                    {formatCurrency(grandTotals.totalDebit)}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: "block" }}
                  >
                    {grandTotals.transactionCount} total transactions
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Grand Total Credit
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 600, color: "text.primary" }}
                  >
                    {formatCurrency(grandTotals.totalCredit)}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: "block" }}
                  >
                    {ledgerData.groups.length} account groups
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Net Balance
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      color:
                        grandTotals.totalDebit >= grandTotals.totalCredit
                          ? "error.main"
                          : "text.primary",
                    }}
                  >
                    {formatCurrency(
                      Math.abs(
                        grandTotals.totalDebit - grandTotals.totalCredit,
                      ),
                    )}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: "block" }}
                  >
                    {grandTotals.totalDebit >= grandTotals.totalCredit
                      ? "Net Debit"
                      : "Net Credit"}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* ── Per-Account Group Tables ── */}
          {ledgerData.groups.map((group, gIdx) => {
            const showPartyCode = isPartyGroup(group);

            return (
              <Box key={group.account_id ?? gIdx} sx={{ mb: 4 }}>
                {/* Account Name Header Bar */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1,
                    backgroundColor: "grey.100",
                    px: 2,
                    py: 1,
                    borderRadius: "4px 4px 0 0",
                    border: "1px solid",
                    borderBottom: "none",
                    borderColor: "grey.300",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {group.account_name}
                    </Typography>
                    {group.account_category && (
                      <Box
                        component="span"
                        sx={{
                          px: 1,
                          py: 0.2,
                          borderRadius: 1,
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          backgroundColor: "grey.300",
                          color: "text.secondary",
                        }}
                      >
                        {group.account_category}
                      </Box>
                    )}
                  </Box>

                  <Box sx={{ display: "flex", gap: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Dr:</strong> {formatCurrency(group.total_debit)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Cr:</strong> {formatCurrency(group.total_credit)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Closing:</strong>{" "}
                      {formatCurrency(group.closing_balance)}{" "}
                      <BalanceBadge type={group.closing_balance_type} />
                    </Typography>
                  </Box>
                </Box>

                {/* Transactions Table */}
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ borderRadius: "0 0 4px 4px" }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "grey.50" }}>
                        <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Voucher No.
                        </TableCell>
                        {showPartyCode && (
                          <TableCell
                            sx={{ fontWeight: 600, whiteSpace: "nowrap" }}
                          >
                            Party Code
                          </TableCell>
                        )}
                        <TableCell sx={{ fontWeight: 600 }}>
                          Narration
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 600, color: "error.main" }}
                        >
                          Debit
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 600, color: "text.primary" }}
                        >
                          Credit
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          Balance
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {/* Opening Balance Row */}
                      <TableRow sx={{ backgroundColor: "grey.50" }}>
                        <TableCell colSpan={showPartyCode ? 4 : 3}>
                          <strong>Opening Balance</strong>
                        </TableCell>
                        <TableCell align="right">-</TableCell>
                        <TableCell align="right">-</TableCell>
                        <TableCell align="right">
                          <strong>
                            {formatCurrency(group.opening_balance)}
                          </strong>{" "}
                          <BalanceBadge type={group.opening_balance_type} />
                        </TableCell>
                      </TableRow>

                      {/* Transaction Rows */}
                      {group.transactions.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={showPartyCode ? 7 : 6}
                            align="center"
                            sx={{ py: 2, color: "text.secondary" }}
                          >
                            No transactions in this date range
                          </TableCell>
                        </TableRow>
                      ) : (
                        group.transactions.map((txn, tIdx) => (
                          <TableRow key={txn.lkey ?? tIdx} hover>
                            <TableCell>
                              {format(new Date(txn.date), "dd/MM/yyyy")}
                            </TableCell>
                            <TableCell>{txn.voucher_number}</TableCell>
                            {showPartyCode && (
                              <TableCell sx={{ whiteSpace: "nowrap" }}>
                                {txn.party_code || "-"}
                              </TableCell>
                            )}
                            <TableCell>{txn.narration}</TableCell>
                            <TableCell
                              align="right"
                              sx={{ color: "error.main" }}
                            >
                              {txn.debit_amount > 0
                                ? formatCurrency(txn.debit_amount)
                                : "-"}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ color: "text.primary" }}
                            >
                              {txn.credit_amount > 0
                                ? formatCurrency(txn.credit_amount)
                                : "-"}
                            </TableCell>
                            <TableCell align="right">
                              <strong>
                                {formatCurrency(txn.running_balance)}
                              </strong>{" "}
                              <BalanceBadge type={txn.balance_type} />
                            </TableCell>
                          </TableRow>
                        ))
                      )}

                      {/* Closing Balance Row */}
                      <TableRow sx={{ backgroundColor: "grey.50" }}>
                        <TableCell colSpan={showPartyCode ? 4 : 3}>
                          <strong>Closing Balance</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>{formatCurrency(group.total_debit)}</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>{formatCurrency(group.total_credit)}</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>
                            {formatCurrency(group.closing_balance)}
                          </strong>{" "}
                          <BalanceBadge type={group.closing_balance_type} />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            );
          })}

          {/* Download PDF */}
          <Box sx={{ mt: 3, textAlign: "right" }}>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={generatePDF}
            >
              Download PDF
            </Button>
          </Box>
        </Paper>
      )}

      {/* Empty State */}
      {!ledgerData && !error && !loadingReport && (
        <Paper elevation={1} sx={{ p: 6, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary">
            Select a cost center and date range, then click "Generate" to view
            the ledger
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default SubaccountLedger;
